import { ALL_MODULES } from '../../config/modules.config';

/**
 * MCP module name → frontend module config id, for the modules whose two names
 * differ. An MCP module name IS the backend module key, i.e. an entry of
 * `ModuleConfig.permissionModules` — so both directions are derived from the
 * registry rather than hand-listed. The hand-listed version had drifted: it
 * covered three of the five divergent modules and silently passed
 * `third_party_services` / `design_system` through as if they were ids.
 *
 * A module administered in sub-scopes contributes one entry per scope, which is
 * why `products` and `contacts` both resolve to `master_data` — the same two
 * backend domains `MODULE_AI_MCP` lists for it below.
 */
export const MCP_TO_MODULE_ID: Record<string, string> = Object.fromEntries(
  ALL_MODULES.flatMap(m =>
    m.permissionModules.filter(name => name !== m.id).map(name => [name, m.id])
  )
);

/**
 * Reverse mapping: frontend module config id → backend MCP module name.
 *
 * One name per module, so a sub-scoped module resolves to its FIRST — the
 * reverse of a one-to-many is arbitrary. Callers that need every domain a module
 * spans read `MODULE_AI_MCP` instead.
 */
export const MODULE_ID_TO_MCP: Record<string, string> = Object.fromEntries(
  ALL_MODULES.filter(m => m.permissionModules[0] !== m.id).map(m => [m.id, m.permissionModules[0]])
);

/**
 * Legacy MCP module names → current runtime keys. Between 2026-05 and the
 * WI 0ba39ef9 fix, master_data's toolsets were registered under dotted keys;
 * chat sessions configured in that window may still carry them in their
 * saved tool config. Runtime keys are always undotted (they prefix Anthropic
 * tool names, which reject dots).
 */
const LEGACY_MCP_MODULE_ALIASES: Record<string, string> = {
  'master_data.products': 'products',
  'master_data.contacts': 'contacts',
};

/**
 * Map a legacy dotted MCP module name to its current runtime key.
 * Pass-through for everything else.
 */
export function normalizeLegacyMcpModuleName(mcpModuleName: string): string {
  return LEGACY_MCP_MODULE_ALIASES[mcpModuleName] ?? mcpModuleName;
}

export function normalizeMcpModuleNames(mcpModuleNames: string[]): string[] {
  return [...new Set(mcpModuleNames.map(normalizeLegacyMcpModuleName))];
}

/**
 * Resolve an MCP module name to a frontend module config ID.
 */
export function resolveModuleId(mcpModuleName: string): string {
  return MCP_TO_MODULE_ID[mcpModuleName] || mcpModuleName;
}

/**
 * Resolve a frontend module config ID to a backend MCP module name.
 */
export function resolveMcpModuleName(moduleId: string): string {
  return MODULE_ID_TO_MCP[moduleId] || moduleId;
}

/**
 * Frontend module config ID → the backend MCP module name(s) whose tools the
 * global Luma assistant enables when its side panel is opened on that module.
 *
 * This is the SINGLE SOURCE for two things GlobalAIButton needs: which UI
 * modules have an in-app Luma assistant (enhanced mode → model picker + MCP
 * config), and which MCP toolset(s) back each one. It is keyed by CURRENT
 * frontend module config ids (see `modules_entities.config.json`) — NOT backend
 * MCP names — so a module rename or consolidation surfaces here (and in its
 * drift-guard test) instead of silently dropping Luma into basic mode.
 *
 * `master_data` is the one UI module that spans two backend MCP domains
 * (products + contacts), so it enables both. Values must match a registered
 * backend chat provider / MCP module name; keys must be real module ids.
 */
export const MODULE_AI_MCP: Record<string, string[]> = {
  tasks: ['tasks'],
  pipeline: ['pipeline'],
  recruiting: ['recruiting'],
  accounting: ['accounting'],
  support: ['support'],
  master_data: ['products', 'contacts'],
  people: ['people'],
  ai: ['ai_agents'],
  ai_chat: ['ai_chat'],
  forms_bpm: ['forms_bpm'],
  website_hosting: ['website_hosting'],
  email: ['email'],
  messaging: ['messaging'],
  files: ['file_management'],
  software_factory: ['software_factory'],
  inventory: ['inventory'],
  marketing: ['marketing'],
  contracts: ['contracts'],
  sales: ['sales'],
  shipments: ['shipments'],
  procurement: ['procurement'],
  billing: ['billing'],
  chatbot: ['chatbot'],
  developer: ['developer'],
  brokerage: ['brokerage'],
};

/**
 * The backend MCP module name(s) Luma should enable for a frontend module, or
 * `[]` if the module has no in-app assistant.
 */
export function getModuleAiMcpModules(moduleId: string): string[] {
  return MODULE_AI_MCP[moduleId] ?? [];
}

/** Whether the given frontend module has an in-app Luma assistant. */
export function moduleHasAiAssistant(moduleId: string): boolean {
  return moduleId in MODULE_AI_MCP;
}
